import type { AuthTokens } from '@mindweb/shared'
import { api } from '@/lib/api'
import { useAuthStore } from '@/lib/store/auth'

const STATE_KEY = 'gh_oauth_state'
const RETURN_KEY = 'gh_oauth_return_to'

function getRedirectUri(): string {
  return `${window.location.origin}/auth/github/callback`
}

function randomState(): string {
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

export function startGithubLogin(returnTo?: string): { ok: true } | { ok: false; error: string } {
  const clientId = import.meta.env.VITE_GITHUB_CLIENT_ID
  if (!clientId) {
    return { ok: false, error: 'GitHub client ID is not configured' }
  }

  const state = randomState()
  sessionStorage.setItem(STATE_KEY, state)
  if (returnTo) sessionStorage.setItem(RETURN_KEY, returnTo)

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: getRedirectUri(),
    scope: 'read:user user:email',
    state,
    allow_signup: 'true',
  })

  window.location.href = `https://github.com/login/oauth/authorize?${params.toString()}`
  return { ok: true }
}

export function consumeReturnTo(): string {
  const v = sessionStorage.getItem(RETURN_KEY) ?? '/'
  sessionStorage.removeItem(RETURN_KEY)
  return v
}

export async function handleGithubCallback(
  code: string,
  state: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const expected = sessionStorage.getItem(STATE_KEY)
  sessionStorage.removeItem(STATE_KEY)

  if (!expected || expected !== state) {
    return { ok: false, error: 'OAuth state mismatch. Please try again.' }
  }

  try {
    const result = await api.post<AuthTokens>('/api/auth/github', {
      code,
      redirectUri: getRedirectUri(),
    })
    if (!result.success || !result.data) {
      return { ok: false, error: result.error ?? 'Authentication failed' }
    }
    useAuthStore.getState().setTokens(result.data.accessToken, result.data.refreshToken)
    return { ok: true }
  } catch (error) {
    console.error('GitHub code exchange failed:', error)
    return { ok: false, error: 'Authentication failed. Please try again.' }
  }
}
